'use client'

import {
  MINIMUM_CHARACTER_SEARCH_TRIGGER,
  ProductSearchInput,
} from '@/feature/product/components/product-search-input/ProductSearchInput.client'
import { toProductPage } from '@/feature/product/services/product.routing'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { useLazyQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { SearchDocument, type SearchQuery } from '@gql-generated/graphql'
import { useCallback, useState } from 'react'

gql(/* GraphQL */ `
  fragment SearchProductsField_Search on Search {
    products {
      name
      urlKey
      reference
      brand {
        name
        urlKey
      }
      mainImage {
        ...FullPicture
      }
    }
  }

  query Search($query: String!) {
    search(query: $query) {
      ...SearchProductsField_Search
      product {
        reference
        urlKey
      }
      categories {
        name
        urlKey
      }
      suggestions {
        label
      }
      brands {
        name
        urlKey
      }
    }
  }
`)

export function SearchInputWithDataLoader({ className }: WithClassName) {
  /** The query for the SearchBar */
  const [searchQuery, { loading, data, error }] = useLazyQuery(SearchDocument)

  const [resultData, setResultData] = useState<SearchQuery | undefined>(undefined)
  const router = useRouter()

  const onSubmitSearch = useCallback(
    async (search: string) => {
      if (search.length < MINIMUM_CHARACTER_SEARCH_TRIGGER) {
        setResultData(undefined)

        return
      }

      const { data: resultData } = await searchQuery({
        variables: {
          query: search,
        },
      })

      setResultData(resultData)

      const hasNoResults =
        !resultData?.search ||
        (!resultData?.search.brands?.length &&
          !resultData?.search.categories?.length &&
          !resultData?.search.products?.length &&
          !resultData?.search.suggestions?.length)

      if (!hasNoResults && search.length >= MINIMUM_CHARACTER_SEARCH_TRIGGER) {
        pushToGtmDataLayer({
          event: 'search',
          eventCategory: 'Recherche Interne - Layer de suggestion',
          eventAction: 'layer displayed',
          eventLabel: search,
        })
      }
    },
    [searchQuery],
  )

  const onSubmitAnalytics = useCallback(
    (search: string) => {
      const dataAnalytics = data?.search

      if (dataAnalytics?.product?.urlKey) {
        const nbResults =
          (dataAnalytics?.product?.urlKey ? 1 : 0) +
          (dataAnalytics?.suggestions?.length ?? 0) +
          (dataAnalytics?.categories?.length ?? 0) +
          (dataAnalytics?.brands?.length ?? 0)

        const eventCategory = `Recherche Interne -  ${nbResults > 0 ? 'Avec' : 'Sans'} Résultats`

        pushToGtmDataLayer({
          event: 'search',
          eventCategory,
          eventAction: search,
          eventLabel: nbResults.toString(),
        })

        router.push(toProductPage(dataAnalytics.product.urlKey))
      }
    },
    [data, router],
  )

  return (
    <ProductSearchInput
      onSubmit={onSubmitSearch}
      onSubmitAnalytics={onSubmitAnalytics}
      loading={loading}
      results={resultData?.search}
      error={!!error}
      className={className}
    />
  )
}
